/**
 * The changeover from one service worker to the next, as the page sees it.
 *
 * The worker calls `skipWaiting()` and `clients.claim()`, so a new deployment
 * takes control of a tab that is still running the previously downloaded
 * bundle. Nothing about that is visible: the page keeps working, against a
 * shell it no longer matches, until browser state is restarted. A production
 * incident ended exactly there, with developer tools as the only way out.
 *
 * So the page listens. Everything here is structural rather than DOM-typed so
 * the lifecycle can be driven directly in tests, which is the only way to
 * exercise a changeover that otherwise needs two deployments and a browser.
 */

/** A worker has finished installing, or has just taken over this page. */
export type UpdateSignal = "ready" | "took-control";

export interface WorkerLike {
  readonly state: string;
  addEventListener(type: "statechange", listener: () => void): void;
}

export interface RegistrationLike {
  readonly installing: WorkerLike | null;
  readonly waiting: WorkerLike | null;
  addEventListener(type: "updatefound", listener: () => void): void;
}

export interface ContainerLike {
  readonly controller: unknown;
  addEventListener(type: "controllerchange", listener: () => void): void;
}

/**
 * Wire every point at which a new shell can appear.
 *
 * A worker that installs with no controller present is this tab's first ever
 * registration, not an update, and announcing it would be a lie.
 */
export function watchForUpdate(
  container: ContainerLike,
  registration: RegistrationLike,
  announce: (signal: UpdateSignal) => void,
): void {
  const announceInstalled = (worker: WorkerLike): void => {
    if (worker.state === "installed" && container.controller) announce("ready");
  };

  // A worker can already be parked here when the page loads, which happens
  // whenever the previous visit downloaded an update and the tab never
  // reloaded. No event will fire for it.
  if (registration.waiting) announceInstalled(registration.waiting);

  registration.addEventListener("updatefound", () => {
    const worker = registration.installing;
    if (!worker) return;
    worker.addEventListener("statechange", () => { announceInstalled(worker); });
  });

  // The failure from the incident: control changes hands under a page that is
  // still executing the old bundle.
  container.addEventListener("controllerchange", () => { announce("took-control"); });
}

/** Work that a reload would interrupt or throw away. */
export interface PendingWork {
  /** Notes saved on this device that the hosted vault has not accepted yet. */
  readonly queuedNotes: number;
  /** Text in an open editor that has not been written to the vault at all. */
  readonly unsavedEditor: boolean;
}

export interface UpdatePrompt {
  readonly message: string;
  /** False while a reload would lose text that exists nowhere else. */
  readonly reloadable: boolean;
}

/**
 * What the reader is told, and whether reloading now is safe.
 *
 * Queued notes are durable — they live in this browser's vault and are still
 * there after a reload — so they never block one. Unsaved editor text does,
 * because reloading out from under someone mid-edit is its own bug.
 */
export function updatePrompt(signal: UpdateSignal, pending: PendingWork): UpdatePrompt {
  const arrival = signal === "took-control"
    ? "A new version of RecallVault has taken over. This page is still running the old one."
    : "A new version of RecallVault is ready.";
  if (pending.unsavedEditor) {
    return { message: `${arrival} Save your note first, then reload.`, reloadable: false };
  }
  if (pending.queuedNotes > 0) {
    const notes = `${pending.queuedNotes} note${pending.queuedNotes === 1 ? "" : "s"}`;
    return { message: `${arrival} ${notes} waiting to sync stay on this device.`, reloadable: true };
  }
  return { message: `${arrival} Reload to use it.`, reloadable: true };
}

export interface ShellCaches {
  registrations(): Promise<readonly { unregister(): Promise<boolean> }[]>;
  keys(): Promise<readonly string[]>;
  remove(key: string): Promise<boolean>;
}

/**
 * The way out of a shell that is stuck hard enough that no update ever lands.
 *
 * Unregistering the worker and dropping its caches leaves the next navigation
 * with nothing to serve but the network. This exists so that recovering from a
 * bad cached shell is a button in the account area rather than developer tools.
 */
export async function discardShell(caches: ShellCaches): Promise<void> {
  const registrations = await caches.registrations();
  await Promise.all(registrations.map((registration) => registration.unregister()));
  const keys = await caches.keys();
  await Promise.all(keys.map((key) => caches.remove(key)));
}
